import React, { useEffect, useRef } from 'react';
import { useApp } from './AppProvider.tsx';
import { translations } from '../translations/translations';
import { ExIcon } from '@boomi/exosphere';
import { signOut } from '../okta';
import AnimatedLink from './AnimatedLink';
import { base64JpgToSrc } from './utils.ts';

import './header.css';

const BoomiHeader = () => {
    const { userInfo, language, currentUserProfileImage } = useApp();
    const servicesMenuWrapper = useRef<HTMLDetailsElement>(null);
    const resourcesMenuWrapper = useRef<HTMLDetailsElement>(null);
    const labsMenuWrapper = useRef<HTMLDetailsElement>(null);
    const allMenuRefs = [servicesMenuWrapper, resourcesMenuWrapper, labsMenuWrapper];

    const username = `${userInfo?.given_name ?? ''} ${userInfo?.family_name ?? ''}`;
    const boomiHeaderNav = [
        {
            title: translations[language].BOOMI_HEADER_services,
            links: [
                {
                    url: 'https://platform.boomi.com/AtomSphere.html',
                    title: translations[language].BOOMI_HEADER_integration_title,
                    description: translations[language].BOOMI_HEADER_integration_description,
                },
                {
                    url: 'https://platform.boomi.com/MdmSphere.html',
                    title: translations[language].BOOMI_HEADER_data_hub_title,
                    description: translations[language].BOOMI_HEADER_data_hub_description,
                },
                {
                    url: 'https://platform.boomi.com/ApiSphere.html',
                    title: translations[language].BOOMI_HEADER_api_management_title,
                    description: translations[language].BOOMI_HEADER_api_management_description,
                },
                {
                    url: 'https://flow.boomi.com/',
                    title: translations[language].BOOMI_HEADER_flow_title,
                    description: translations[language].BOOMI_HEADER_flow_description,
                },
                {
                    url: 'https://platform.boomi.com/EventStreams.html',
                    title: translations[language].BOOMI_HEADER_event_streams_title,
                    description: translations[language].BOOMI_HEADER_event_streams_description,
                },
            ],
            reference: servicesMenuWrapper,
        },
        {
            title: translations[language].BOOMI_HEADER_discover_title,
            url: 'https://discover.boomi.com/browse-solutions',
        },
        {
            title: translations[language].BOOMI_HEADER_resources,
            links: [
                {
                    url: 'https://help.boomi.com/',
                    title: translations[language].BOOMI_HEADER_documentation_title,
                    description: translations[language].BOOMI_HEADER_documentation_description,
                },
                {
                    url: 'https://community.boomi.com/s/support/',
                    title: translations[language].BOOMI_HEADER_support_title,
                    description: translations[language].BOOMI_HEADER_support_description,
                },
                {
                    url: 'https://community.boomi.com/s/',
                    title: translations[language].BOOMI_HEADER_community_title,
                    description: translations[language].BOOMI_HEADER_community_description,
                },
                {
                    url: 'https://train.boomi.com/',
                    title: translations[language].BOOMI_HEADER_training_title,
                    description: translations[language].BOOMI_HEADER_training_description,
                },
                {
                    url: 'https://billing.stripe.com/p/login/aEU6s02oi0Gw7IIcMM',
                    title: translations[language].BOOMI_HEADER_usage_and_billing_title,
                    description: translations[language].BOOMI_HEADER_usage_and_billing_description,
                },
            ],
            reference: resourcesMenuWrapper,
        },
        {
            title: translations[language].BOOMI_HEADER_labs,
            links: [
                {
                    url: 'https://boomi.com/boomi-labs/#spaces',
                    title: translations[language].BOOMI_HEADER_spaces_title,
                    description: translations[language].BOOMI_HEADER_spaces_description,
                },
                {
                    url: 'https://platform.boomi.com/BoomiLabs.html#pub_bundles',
                    title: translations[language].BOOMI_HEADER_bundles_title,
                    description: translations[language].BOOMI_HEADER_bundles_description,
                },
                {
                    url: 'https://platform.boomi.com/BoomiLabs.html#pub_ipacks',
                    title: translations[language].BOOMI_HEADER_integration_packs_title,
                    description: translations[language].BOOMI_HEADER_integration_packs_description,
                },
                {
                    url: 'https://platform.boomi.com/BoomiLabs.html#pub_pl',
                    title: translations[language].BOOMI_HEADER_process_library_title,
                    description: translations[language].BOOMI_HEADER_process_library_description,
                },
                {
                    url: 'https://platform.boomi.com/AtomSphere.html#settings',
                    title: translations[language].BOOMI_HEADER_connectors_title,
                    description: translations[language].BOOMI_HEADER_connectors_description,
                },
            ],
            reference: labsMenuWrapper,
        },
    ];

    // Toggle the menu we clicked and close any other open menus.
    const closeOpenMenus = (clickedMenuRef: React.RefObject<HTMLDetailsElement>) => {
        allMenuRefs.forEach((otherMenuRef) => {
            if (
                otherMenuRef.current !== clickedMenuRef.current &&
                otherMenuRef.current?.hasAttribute('open')
            ) {
                otherMenuRef.current.removeAttribute('open');
            }
        });
    };

    // Close all open menus if clicking elsewhere on the page.
    useEffect(() => {
        const handleBoomiNavOutsideClicks = (event: MouseEvent) => {
            if (
                !(event.target as Element).classList.contains('boomi-menu-trigger') &&
                !(event.target as Element).classList.contains('boomi-menu-wrapper')
            ) {
                allMenuRefs.forEach((menuRef) => {
                    menuRef.current?.hasAttribute('open') &&
                        menuRef.current?.removeAttribute('open');
                });
            }
        };
        document.addEventListener('click', handleBoomiNavOutsideClicks);

        return () => {
            document.removeEventListener('click', handleBoomiNavOutsideClicks);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <header className="boomi-header">
            <img className="boomi-logo" src="/img/b-mark.svg" alt="Boomi" />

            <nav className="boomi-header-nav">
                {boomiHeaderNav.map((menu) =>
                    menu.links ? (
                        <details
                            className="boomi-menu-wrapper"
                            ref={menu.reference}
                            key={menu.title}
                        >
                            <summary
                                className="boomi-menu-trigger"
                                onClick={() => closeOpenMenus(menu.reference)}
                            >
                                {menu.title}
                                <ExIcon
                                    icon="direction-arrowhead-down"
                                    className="boomi-menu-trigger-icon"
                                ></ExIcon>
                            </summary>
                            <ul className="boomi-menu-list">
                                {menu.links.map((link) => (
                                    <li className="boomi-menu-item" key={link.title}>
                                        <a
                                            className="boomi-menu-item-link"
                                            href={link.url}
                                            target="_blank"
                                            rel="noreferrer noopener"
                                        >
                                            <strong className="boomi-menu-item-title">
                                                {link.title}
                                            </strong>
                                            <p className="boomi-menu-item-description">
                                                {link.description}
                                            </p>
                                            <ExIcon
                                                className="boomi-menu-item-icon"
                                                icon="new-window"
                                            ></ExIcon>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </details>
                    ) : (
                        <a
                            key={menu.title}
                            className="boomi-menu-trigger"
                            href={menu.url}
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            {menu.title}
                        </a>
                    ),
                )}
            </nav>

            <section className="boomi-header-addons">
                <a
                    className="boomi-addon-help"
                    href="https://help.boomi.com/"
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    <ExIcon icon="Help" className="boomi-addon-icon" hide-browser-tooltip></ExIcon>
                </a>

                {/* Commenting this out for now
                    <a
                        className="boomi-menu-trigger"
                        href="#"
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        <ExIcon
                            icon="Message"
                            className="boomi-addon-icon"
                            hide-browser-tooltip
                        ></ExIcon>
                    </a>
                    */}

                <AnimatedLink className="boomi-addon-user user-button" to="/profile">
                    {currentUserProfileImage ? (
                        <img
                            className="user-avatar"
                            src={base64JpgToSrc(currentUserProfileImage)}
                            alt="User avatar"
                        />
                    ) : (
                        <ExIcon
                            className="user-avatar-blank"
                            icon="User"
                            hide-browser-tooltip
                        ></ExIcon>
                    )}
                    <span className="user-name">{username}</span>
                </AnimatedLink>

                <button className="boomi-addon-logout" role="link" onClick={signOut}>
                    {translations[language].USER_MENU_sign_out}
                </button>
            </section>
        </header>
    );
};

export default BoomiHeader;
